/**
 * When users clicks in an editable element, the browser places a cursor (a.k.a. insertion point) in
 * the nearest text node that is within the clicked element, on the same line as your click.
 * The text node may be either directly within the clicked element, or in one of its child elements.
 *
 * The problem is that Webkit browsers (Chrome, Safari, Opera) exhibit this same behavior when
 * contenteditable is set on the child instead of the parent. The browser shouldn't even bother
 * looking for the nearest text node in this case since the element user actually clicked on isn't
 * editable. But Webkit does, and if that text node happens to be in the editable child, user gets
 * a blinking cursor.
 *
 * So, Webkit browsers are doing this on click:
 * 1. find nearest text node within clicked element;
 * 2. if text node is editable:
 *    1. add insertion point;
 *
 * ...when they should be doing this:
 * 1. if clicked element is editable:
 *    1. find nearest text node within clicked element;
 *    1. add insertion point;
 *
 * There are different ways to bypass this with different workarounds. But, since our core should
 * be able to work with any mark, we are applying this JS fix: We disable the editing behavior
 * if element loses the focus, and enable it only if element is clicked.
 */

import bowser from 'yola-bowser';
import WSInternalEvent from '../../../utils/helpers/ws-internal-event';
import text from '../../bindings/text';
import getScrollableElement from '../../../../view/helpers/get-scrollable-element';
import getContenteditableAttributeValue from '../../../utils/helpers/webkit-workarounds/get-contenteditable-attribute-value';

const pauseAll = (liveContainer) => {
  if (!bowser.chrome && !bowser.chromium && !bowser.opera && !bowser.safari) return;

  const contenteditableElements = liveContainer.querySelectorAll('[contenteditable]');

  contenteditableElements.forEach((element) => {
    element.setAttribute('contenteditable', 'false');
  });
};

const pauseByEvent = WSInternalEvent.bind(undefined, (e) => {
  if (!bowser.chrome && !bowser.chromium && !bowser.opera && !bowser.safari) return;

  const contenteditableElement = text.getRootTextElement(e.target);

  if (!contenteditableElement) return;

  contenteditableElement.setAttribute('contenteditable', 'false');
});

const resumeByEvent = WSInternalEvent.bind(undefined, (e) => {
  if (!bowser.chrome && !bowser.chromium && !bowser.opera && !bowser.safari) return;
  const contenteditableElement = text.getRootTextElement(e.target);

  if (!contenteditableElement) return;

  const contenteditableValue = getContenteditableAttributeValue(contenteditableElement);
  contenteditableElement.setAttribute('contenteditable', contenteditableValue);
  const scrollableElement = getScrollableElement(contenteditableElement.ownerDocument);

  const { scrollTop, scrollLeft } = scrollableElement;

  contenteditableElement.focus({
    preventScroll: true,
  });

  scrollableElement.scrollTo(scrollLeft, scrollTop);
});

export default {
  pauseAll,
  pauseByEvent,
  resumeByEvent,
};
